import React, { useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'

import ChartCard from './components/ChartCard'
import ModuleAudienceCard from './components/ModuleAudienceCard'
import Section from './components/Section'
import StatTilesCard from './components/StatTilesCard'
import { AXIS_TICK, CHART_COLORS, TOOLTIP_STYLE } from './constants'
import { OPERATION_PRODUCT_ANALYTICS_ACCESS_POINT_ZONES } from './operations-gql/queries'
import {
  AccessPointZonesActivity,
  AccessPointZonesQuery,
  ProductAnalyticsPeriod,
  ProductAnalyticsVariables,
} from './types'

type AccessPointZonesSectionProps = {
  period: ProductAnalyticsPeriod
}

type ActivityTile = {
  key: keyof AccessPointZonesActivity
  labelKey: string
  suffix?: string
}

const TRANSLATION_PREFIX = 'stats.product_analytics.access_point_zones'

const RADIO_ACTIONS: ActivityTile[] = [
  { key: 'deviceDisabled', labelKey: 'device_disabled' },
  { key: 'floorDisabled', labelKey: 'floor_disabled' },
  { key: 'radioEnabled', labelKey: 'radio_enabled' },
]

const IMPACT_TILES: ActivityTile[] = [
  { key: 'disabledDevices', labelKey: 'disabled_devices' },
  { key: 'impactedStations', labelKey: 'impacted_stations' },
]

const RELIABILITY_TILES: ActivityTile[] = [
  { key: 'disableOpened', labelKey: 'disable_opened' },
  { key: 'disableAbandonmentRate', labelKey: 'disable_abandonment_rate', suffix: ' %' },
  { key: 'radioFailed', labelKey: 'radio_failed' },
  { key: 'radioFailureRate', labelKey: 'radio_failure_rate', suffix: ' %' },
]

const AccessPointZonesSection: React.FC<AccessPointZonesSectionProps> = ({ period }) => {
  const { t } = useTranslation()
  const { data, loading, error } = useQuery<AccessPointZonesQuery, ProductAnalyticsVariables>(
    OPERATION_PRODUCT_ANALYTICS_ACCESS_POINT_ZONES,
    { variables: { period }, errorPolicy: 'all' }
  )
  const audience = data?.productAnalytics?.accessPointZonesAudience
  const activity = data?.productAnalytics?.accessPointZonesActivity

  const radioActions = useMemo(() => (
    RADIO_ACTIONS.map(({ key, labelKey }) => ({
      label: t(`${TRANSLATION_PREFIX}.radio_actions.${labelKey}`),
      value: activity?.[key] ?? 0,
    }))
  ), [activity, t])

  const hasFieldError = (field: unknown) => Boolean(error) && !field

  const toStatTiles = (tiles: ActivityTile[]) => tiles.map(({ key, labelKey, suffix }) => ({
    key,
    label: t(`${TRANSLATION_PREFIX}.${labelKey}`),
    value: activity?.[key],
    suffix,
  }))

  return (
    <Section id="product-analytics-access-point-zones" title={t(`${TRANSLATION_PREFIX}.title`)} columns={{ xs: 1, md: 2 }}>
      <ModuleAudienceCard
        description={t(`${TRANSLATION_PREFIX}.audience_description`)}
        audience={audience}
        loading={loading}
        hasError={hasFieldError(audience)}
      />

      <ChartCard
        title={t(`${TRANSLATION_PREFIX}.radio_actions_title`)}
        description={t(`${TRANSLATION_PREFIX}.radio_actions_description`)}
        loading={loading}
        hasError={hasFieldError(activity)}
        isEmpty={!radioActions.some(({ value }) => value > 0)}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={radioActions}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} interval={0} />
            <YAxis allowDecimals={false} tick={AXIS_TICK} tickLine={false} axisLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: '#F9FAFB' }} />
            <Bar
              dataKey="value"
              name={t(`${TRANSLATION_PREFIX}.actions`)}
              fill={CHART_COLORS.senary}
              radius={[6, 6, 0, 0]}
              barSize={40}
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <StatTilesCard
        title={t(`${TRANSLATION_PREFIX}.impact`)}
        description={t(`${TRANSLATION_PREFIX}.impact_description`)}
        tiles={toStatTiles(IMPACT_TILES)}
        loading={loading}
        hasError={hasFieldError(activity)}
      />

      <StatTilesCard
        title={t(`${TRANSLATION_PREFIX}.reliability`)}
        description={t(`${TRANSLATION_PREFIX}.reliability_description`)}
        tiles={toStatTiles(RELIABILITY_TILES)}
        loading={loading}
        hasError={hasFieldError(activity)}
      />
    </Section>
  )
}

export default AccessPointZonesSection
